import { ChangeEvent, FormEvent, useMemo, useState } from "react";
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import { useUnidadeNegocioGuard } from "../../../common/hooks/useUnidadeNegocioGuard";
import PageLayout from "../../../common/layouts/pageLayout";
import { Form } from "react-final-form";
import FormHeader from "../../../common/components/forms/formHeader";
import FormHeaderMedium from "../../../common/components/forms/formHeaderMedium";
import FileDragAndDrop from "../../../common/components/fileDragAndDrop";
import { Button, Grid, Typography } from "@mui/material";
import { processMartinsLogEDI } from "./utils/processMartinsEDI";
import { useTranslation } from "react-i18next";
import QCXSelectServicoAutocomplete from "../../../../shared-components/select-servico/QCXSelectServicoAutocomplete";
import { GridColDef } from "@mui/x-data-grid";
import DataTable from "../../../common/components/dataTable";
import { useCreateFaturaMartinsLog } from "./martinsLog.hooks";

const MartinsLogEdiPage = () => {
    useUnidadeNegocioGuard();

    const { t } = useTranslation();

    const [file, setFile] = useState<File | undefined>();
    const [fileData, setFileData] = useState<any>();

    const [createFaturaMartinsLog, isLoading, isError] = useCreateFaturaMartinsLog({
        defaultErrorMessage: 'Erro ao gerar fatura. Verifique os dados do arquivo.',
        defaultSuccessMessage: 'Fatura criada com sucesso! 997 gerado.',
    });

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>, selectedFile: File) => {
        setFile(selectedFile);
    };

    const processFile = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            if (file) {
                console.log('Iniciando a leitura do arquivo:', file.name);

                const reader = new FileReader();

                reader.onload = (event) => {
                    const textContent = event.target?.result as string;

                    if (textContent) {
                        const resultadoExtraido = processMartinsLogEDI(textContent);

                        setFileData(resultadoExtraido);
                        console.log("Dados extraídos com sucesso:", resultadoExtraido);
                    }
                };

                reader.readAsText(file, 'utf-8');
            }
        } catch (error) {
            console.error('Erro ao processar o arquivo:', error);
        }
    };

    const handleSubmit = async (values: any) => {
        if (fileData && fileData.faturas && values?.servico?.id) {

            const primeiraFaturaChave = Object.keys(fileData.faturas)[0];
            const codigoImportador = fileData.faturas[primeiraFaturaChave]?.codigoImportador;

            createFaturaMartinsLog({
                requestPayload: {
                    ...fileData,
                    servicoId: values.servico.id
                },
                queryParams: { clientId: codigoImportador },
            },
                {
                    onSuccess: async (response: any) => {
                        console.log("Resposta bruta:", response);

                        let dadosFormatados = response;
                        if (typeof response === 'string') {
                            try {
                                dadosFormatados = JSON.parse(response);
                            } catch (e) {
                                console.error("Erro ao tentar converter a resposta para JSON:", e);
                            }
                        }

                        // Lemos todas as possíveis saídas
                        const isZip = dadosFormatados?.isZip || dadosFormatados?.data?.isZip;
                        const arquivoZipado = dadosFormatados?.arquivoZipado || dadosFormatados?.data?.arquivoZipado;
                        const arquivoTextoBase64 = dadosFormatados?.arquivoTextoBase64 || dadosFormatados?.data?.arquivoTextoBase64;
                        const nomeArquivo = dadosFormatados?.nomeArquivo || dadosFormatados?.data?.nomeArquivo || 'retorno_martins_log.txt';

                        try {
                            if (isZip && arquivoZipado) {
                                console.log("Baixando lote de arquivos em ZIP...");
                                
                                const byteCharacters = atob(arquivoZipado);
                                const byteNumbers = new Array(byteCharacters.length);
                                for (let i = 0; i < byteCharacters.length; i++) {
                                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                                }
                                const byteArray = new Uint8Array(byteNumbers);
                                const blob = new Blob([byteArray], { type: 'application/zip' });

                                const fileURL = URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = fileURL;
                                link.setAttribute('download', nomeArquivo); // Usa o nome com .zip do back

                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                setTimeout(() => URL.revokeObjectURL(fileURL), 200);

                            } else if (!isZip && arquivoTextoBase64) {
                                console.log("Baixando arquivo de texto EDI único...");
                                
                                const byteCharacters = atob(arquivoTextoBase64);
                                const byteNumbers = new Array(byteCharacters.length);
                                for (let i = 0; i < byteCharacters.length; i++) {
                                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                                }
                                const byteArray = new Uint8Array(byteNumbers);
                                const blob = new Blob([byteArray], { type: 'application/octet-stream' });

                                const fileURL = URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = fileURL;
                                link.setAttribute('download', nomeArquivo);

                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                setTimeout(() => URL.revokeObjectURL(fileURL), 200);

                            } else {
                                console.warn("Nenhum arquivo retornado para download.", dadosFormatados);
                            }
                        } catch (error) {
                            console.error(`Erro durante o processamento do download:`, error);
                        }

                        handleCancel();
                    },
                    onError: (error) => {
                        console.error("Falha ao criar fatura:", error);
                    }
                }
            );
        }
    };

    const handleCancel = () => {
        setFile(undefined);
        setFileData(undefined);
    };

    const faturasColumns = useMemo(() => {
        return [
            { field: 'numeroDaFatura', headerName: 'N° da Fatura', flex: 1 },
            { field: 'codigoImportador', headerName: 'Importador', flex: 1 },
            { field: 'moedaNegociada', headerName: 'Moeda', flex: 1 },
            { field: 'numeroItens', headerName: 'N° de Itens', flex: 1 },
        ] as GridColDef[];
    }, []);

    const faturasData = useMemo(() => {
        if (!fileData || !fileData.faturas) return [];

        return Object.keys(fileData.faturas).reduce((acumulador: any[], key, index) => {
            const fatura = fileData.faturas[key];

            if (fatura) {
                acumulador.push({
                    id: index,
                    numeroDaFatura: fatura.numeroDaFatura || key,
                    codigoImportador: fatura.codigoImportador || 'Não Encontrado',
                    moedaNegociada: fatura.moedaNegociada || 'N/A',
                    numeroItens: fatura.itens?.length ?? 0,
                });
            }

            return acumulador;
        }, []);
    }, [fileData]);

    return (
        <PageLayout title={'Leitura Martins-log EDI'} icon={<HistoryEduIcon color={'secondary'} />}>
            <>
                <Form onSubmit={handleSubmit}>
                    {({ values }) => (
                        <>
                            <form onSubmit={processFile}>
                                <Grid container spacing={2}>
                                    <FormHeader>Importação de Arquivo - Martins-log</FormHeader>

                                    <Grid item xs={12}>
                                        <QCXSelectServicoAutocomplete
                                            id="autocomplete-select-servico-field"
                                            key="autocomplete-select-servico-field"
                                            name="servico.id"
                                            label={t('com.muralis.qcx.servico.labelSingular')}
                                            initialValues={undefined}
                                            produto="FATURA"
                                            required
                                        />
                                    </Grid>

                                    <FormHeaderMedium>Seleção de arquivo</FormHeaderMedium>

                                    <Grid item xs={12}>
                                        <FileDragAndDrop
                                            accept={'.txt, .csv'}
                                            onChangeAction={handleFileChange}
                                        />
                                    </Grid>

                                    {file && (
                                        <Grid item xs={12}>
                                            <Typography variant="body2" color="textSecondary">
                                                Arquivo selecionado: <strong>{file.name}</strong>
                                            </Typography>
                                        </Grid>
                                    )}

                                    <Grid item xs={12} textAlign="right">
                                        <Button
                                            type="submit"
                                            color="secondary"
                                            variant="contained"
                                            disabled={!file}
                                        >
                                            Importar arquivo
                                        </Button>
                                    </Grid>
                                </Grid>
                            </form>

                            {fileData && (
                                <Grid container spacing={2} mt={2}>

                                    <FormHeaderMedium>Revisão das Faturas Lidas</FormHeaderMedium>
                                    <Grid item xs={12}>
                                        <DataTable
                                            columns={faturasColumns}
                                            rows={faturasData}
                                        />
                                    </Grid>


                                    <Grid item xs={12} textAlign="right">
                                        <Button
                                            color="secondary"
                                            variant="contained"
                                            style={{ margin: '0 10px 0 0' }}
                                            onClick={handleCancel}
                                            disabled={isLoading}
                                        >
                                            Cancelar
                                        </Button>
                                        <Button
                                            color="secondary"
                                            variant="contained"
                                            onClick={() => handleSubmit(values)}
                                            disabled={isLoading}
                                        >
                                            {isLoading ? 'Salvando...' : 'Salvar'}
                                        </Button>
                                    </Grid>
                                </Grid>
                            )}
                        </>
                    )}
                </Form>
            </>
        </PageLayout>
    );
};

export default MartinsLogEdiPage;
